import { ButtonGroup, Flex, Text } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm, useFormContext } from "react-hook-form";

import { Restricted } from "@/app/components/Restricted";
import FormReset from "@/common/components/Form/FormReset";
import { FormRootError } from "@/common/components/Form/FormRootError";
import FormSubmit from "@/common/components/Form/FormSubmit";
import FormSwitch from "@/common/components/Form/FormSwitch";
import { ManagedFormControl } from "@/common/components/Form/ManagedFormControl";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useFormMutationSubmitHandler } from "@/common/hooks/useApiForm";
import { useSlugParam } from "@/common/hooks/useParam";
import { useFeatureData } from "@/feature/data/useFeatureData";
import { useFeatureUpdateMutation } from "@/feature/data/useFeatureUpdateMutation";
import { SlackChannelAutocompleteSelect } from "@/integration/components/slack/SlackChannelAutocompleteSelect";
import SlackConnectionGuard from "@/integration/components/slack/SlackConnectionGuard";
import {
  SlackIntegrationSchema,
  SlackIntegrationType,
} from "@reflag/shared/featureAPI";
import { z } from "@reflag/shared/utils/zod";

const defaultValues = {
  feedbackNotification: true,
  targetingRulesChangeNotification: true,
  firstDataNotification: false,
} satisfies z.infer<typeof SlackIntegrationSchema>;

export function SettingsSlack() {
  const { org } = useEnvironment();
  const { featureId } = useSlugParam("feature");
  const { data: feature } = useFeatureData(featureId!);

  const form = useForm<z.input<typeof SlackIntegrationSchema>>({
    resolver: zodResolver(SlackIntegrationSchema),
    mode: "onChange",
    defaultValues: feature?.integrations.slack || defaultValues,
  });

  const updateMutation = useFeatureUpdateMutation(featureId!);
  const submitHandler = useFormMutationSubmitHandler(
    form,
    updateMutation,
    (result) => {
      form.reset(result.integrations.slack);
    },
    {
      prepareVariables: (values) => ({
        id: featureId!,
        integrations: { slack: values },
      }),
    },
  );

  return (
    <Flex direction="column" gap={4} maxW="mediumForm">
      <Restricted alertStyle="callout" feature={feature} role="contributor">
        <SlackConnectionGuard productionOnly>
          <FormProvider {...form}>
            <Flex
              as="form"
              direction={"column"}
              gap={4}
              onSubmit={submitHandler}
            >
              <FormSlackIntegration />
              <FormRootError />
              {org.integrations.slack && (
                <ButtonGroup>
                  <FormSubmit showPostSubmit />
                  <FormReset />
                </ButtonGroup>
              )}
            </Flex>
          </FormProvider>
        </SlackConnectionGuard>
      </Restricted>
    </Flex>
  );
}

function FormSlackIntegration() {
  const { org, env } = useEnvironment();
  const {
    formState: { isSubmitting },
    watch,
  } = useFormContext<SlackIntegrationType>();

  const isDisabled =
    isSubmitting || !env.isProduction || !org.integrations.slack;

  const hasSlackChannel = watch("slackChannel") !== undefined;

  return (
    <Flex direction="column" gap={4}>
      <ManagedFormControl
        isDisabled={!org.integrations.slack}
        label="Slack channel"
        name="slackChannel"
        render={({ field }) => (
          <SlackChannelAutocompleteSelect
            {...field}
            isDisabled={isDisabled}
            canClear
          />
        )}
      />

      <Flex direction="column">
        <FormSwitch
          isDisabled={isDisabled || !hasSlackChannel}
          label="Notify when feedback is submitted"
          name="feedbackNotification"
        />
        <Text color="dimmed" fontSize="sm" maxW="xs">
          An immediate notification when a user leaves feedback on this feature.
        </Text>
      </Flex>
    </Flex>
  );
}
